import { FormValues, IHomeAddress, IPhone } from '../interface/setup';

const text = (value: FormValues[string]) => (typeof value === 'string' ? value.trim() : '');

const toNumberOrNull = (value: FormValues[string]) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const phoneToForm = (phone?: IPhone | null): FormValues => ({
  countryCode: phone?.countryCode ?? '',
  phoneNumber: phone?.phoneNumber ?? '',
});

export const phoneFromForm = (form: FormValues): IPhone => ({
  countryCode: text(form.countryCode),
  phoneNumber: text(form.phoneNumber),
});

// lat/lng aren't shown in the collection forms but ride along in the form
// values, so editing an address never wipes the pin the app already saved.
export const addressToForm = (address?: IHomeAddress | null): FormValues => ({
  address: address?.address ?? '',
  secondaryAddress: address?.secondaryAddress ?? '',
  city: address?.city ?? '',
  state: address?.state ?? '',
  postalCode: address?.postalCode ?? '',
  country: address?.country ?? '',
  lat: address?.lat ?? null,
  lng: address?.lng ?? null,
});

export const addressFromForm = (form: FormValues): IHomeAddress => ({
  address: text(form.address),
  secondaryAddress: text(form.secondaryAddress),
  city: text(form.city),
  state: text(form.state),
  postalCode: toNumberOrNull(form.postalCode),
  country: text(form.country),
  lat: toNumberOrNull(form.lat),
  lng: toNumberOrNull(form.lng),
});

// For PUT /seniors/profile: its address schema rejects '' and null (unlike the contact
// endpoints), so blank fields are left out; the backend replaces the whole address anyway.
export const addressFromFormCompact = (form: FormValues): IHomeAddress =>
  Object.fromEntries(
    Object.entries(addressFromForm(form)).filter(([, value]) => value !== '' && value !== null && value !== undefined)
  );

// Multipart body for the endpoints that also take an avatar: object values go
// as JSON strings (the backend's Joi parses them), booleans as true/false.
export const toMultipart = (values: Record<string, FormValues[string] | object>) => {
  const body = new FormData();
  Object.entries(values).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (value instanceof File) body.append(key, value);
    else if (typeof value === 'object') body.append(key, JSON.stringify(value));
    else body.append(key, String(value));
  });
  return body;
};

// The app's address inputs; the rest of the address (line 2, state, postal code, lat/lng)
// isn't shown, but is kept in the form values so editing never wipes what the app saved.
export const ADDRESS_FIELDS = [
  { name: 'address', labelKey: 'pages.setup.fields.address', type: 'text' },
  { name: 'city', labelKey: 'pages.setup.fields.city', type: 'text' },
  { name: 'country', labelKey: 'pages.setup.fields.country', type: 'text' },
] as const;

